// To debug code press ctrl-alt-0 while the viewer has focus. To debug while starting set the debug flag to true. 
import '../node_modules/pdfjs-dist/web/pdf_viewer.css';
import '../assets/docavea_icon_font.css';
import './fonts.css';
import * as _ from 'lodash';
import { version as viewerVersion } from '../package.json';
import blackNameLogo = require('../assets/namelogo black.svg');
import whiteNameLogo = require('../assets/namelogo white.svg');
import { debug } from './debug';
import { initChannelMessaging, sendResponse, setViewerId } from './send-response';
import { Utils } from './shared/extensions/utils.extension';
import {
    ClickEvent, DualPage,
    FindMatchRectsPayload,
    FindPayload, FindResponse,
    GetImageRectsPayload,
    GetPageRectsPayload,
    LoadAndThumbPayload, PdfLosses, LoadPdfPayload, RequestType,
    ResponseType, SrcType,
    ThumbnailPayload, ViewerRequest,
    WheelPayload, Zoom, DOCAVEA_VIEWERS_IN_TABS_OFFSET,
} from './shared/model/docavea-viewer-interface';
import { findMatchRects } from './find-match-rects';
import { resolveHost } from './host';
import { dataUrlBytes, InlineWasmFactory } from './wasm';
import { getImageRects } from './image-rects';
import { getPageRects } from './page-rects';
import { cachedScan, hideRemovedMarks } from './redaction-mode';
import { canSelectText, removeBadRectangles, selectionHandler } from './textlayer';
// import { canSelectText, removeBadRectangles, selectionHandler, iterateNodes } from './textlayer';
import { makeDocThumbnail, makeLoadThumbnail, makeQuickView } from './thumbnail';
import { viewChanged, clearDivs, setSplitMarks, setDeleteMarks } from './view-changed';
import { calculateCurrentScale, initMouseWheelZoom, initMouseWheelZoomEvents, wheelZoom } from './zoom-mouse-wheel';
import { initPinchZoom } from './zoom-pinch';

// **** Copied from pdfjs ui_utils.js **********************
const SpreadMode = {
    UNKNOWN: -1,
    NONE: 0, // Default value.
    ODD: 1,
    EVEN: 2,
};
const TextLayerMode = {
    DISABLE: 0,
    ENABLE: 1,
    ENABLE_ENHANCE: 2,
};

/* possible values for pdfViewer.currentScaleValue
'page-actual'
'page-width'
'page-height'
'page-fit'
'auto'
or a number setting the scale
... we don't use the stings as it makes it hard when manual scaling is requested, instead the scale is always calculated
*/

// **** end pdfjs ui_utils.js copy *************************

// Global variables
export const TEST_MODE = false;  // when true file is loaded from URL and a host app is not needed
export let selectedPageBorder: string;
export let pagesinit = false;  // false until the pdfjs viewer has initialize with a new document. Nothing can be be done before it is true
export let pdfViewer: any;
export let pdfDocument: any;
export let loadPdfPayload: LoadPdfPayload;
// From the last LOAD_PDF (visiodocs-app#584): the member may not upload, so a file dragged over the viewer gets the
// no-drop cursor and a drop is not passed on. False until an app that sends it says otherwise
let fileDropDisabled = false;
export let loadAndThumbPayload: LoadAndThumbPayload;
export const pageRotations: number[] = [];
export let viewerId: number;
export let latestRequest: string;
export let debugOn = false;
export let viewerMode: 'iframe' | 'viewerTab' = 'iframe'; // default is iframe, set to viewerTab if the viewer is opened in a new tab
export let toolbarHeight = 0; // height of the toolbar in pixels, used to calculate scroll offsets
const pageNavigationElements: {
    pageInput: HTMLInputElement | null;
    prevButton: HTMLButtonElement | null;
    nextButton: HTMLButtonElement | null;
    backButton: HTMLButtonElement | null;
    forwardButton: HTMLButtonElement | null;
} = { pageInput: null, prevButton: null, nextButton: null, backButton: null, forwardButton: null };
// Page history tracking for back/forward navigation
const pageHistory: number[] = [];
let pageHistoryIndex = -1;
// Progress indicator for viewerTab mode
let progressOverlay: HTMLDivElement | null = null;
let progressText: HTMLSpanElement | null = null;
// Bookmark sidebar for viewerTab mode
let bookmarkSidebar: HTMLDivElement | null = null;
let bookmarkContent: HTMLDivElement | null = null;
export let sidebarOpen = false;
export let sidebarWidth = 250; // Current sidebar width in pixels
let pdfOutline: any[] | null = null;
let updateResizeHandlePosition: (() => void) | null = null;
// Find bar for viewerTab mode
let findBar: HTMLDivElement | null = null;
let findInput: HTMLInputElement | null = null;
let findMatchLabel: HTMLSpanElement | null = null;
let findBarOpen = false;
let lastFindQuery = '';
let findDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
let currentTheme: string[] = []; // Store current theme for use in outline rendering
// Module variables
let pdfjsLib: any;
let pdfjsViewer: any;
let eventBus: any;          // pdfjs > 2.4 requires an eventBus!?
let pdfLoading = false;
let pdfFindController;
let pdfLinkService;
let viewerDocName: string;
// let l10n;
let searchTimeout;
let pageNumberBeforeRotate = 1;
let loadAndThumb = false;
let textCopyModeEnabled = false;
let oldZoom: Zoom;
let pdfViewerAbort: AbortController | undefined;
let pdfViewerRedactionMode = false;     // the annotation mode the current PDFViewer was made with (visiodocs-app#492)
const redactionModeStyle = document.createElement('style');
let offlineLoadParams: object | undefined;  // the offline build's getDocument options (worker, wasm), set in viewerMain

// Entry point
viewerMain();

// Helper function used to check if the viewer is actually ready (this is a hack)
// If is called when pdfDocument is undefined it returns 0
export const pdfReady = () => {
    return new Promise<number>((resolve) => {
        if (pdfDocument) {
            pdfDocument.getPage(pdfViewer.currentPageNumber).then((pageNumber: number) => {
                resolve(pageNumber);
            });
        } else {
            resolve(-1);
        }
    });
};

export function delay(timeout: number) {
    return new Promise<void>((resolve) => {
        setTimeout(() => {
            return resolve();
        }, timeout);
    });
}

async function viewerMain() {
    pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
    pdfjsViewer = await import('pdfjs-dist/web/pdf_viewer.mjs');
    // Specify the workerSrc property.
    // pdfjsLib.GlobalWorkerOptions.workerSrc = 'build/pdf.worker.mjs';
    if (OFFLINE) {
        // The one-file viewer (visiodocs-app#655): a page opened from file:// can fetch nothing and start no module
        // worker, so the worker is in this file as a classic script, started from a blob, and the wasm comes inlined.
        // The requires sit inside `if (OFFLINE)` so the online build, where OFFLINE is false, never resolves them
        const workerSource: string = require('../dist-offline/pdf.worker.iife.js');
        const wasmFiles = {
            'openjpeg.wasm': dataUrlBytes(require('pdfjs-dist/wasm/openjpeg.wasm')),
            'qcms_bg.wasm': dataUrlBytes(require('pdfjs-dist/wasm/qcms_bg.wasm')),
        };
        const port = new Worker(URL.createObjectURL(new Blob([workerSource], { type: 'text/javascript' })));
        offlineLoadParams = {
            // One PDFWorker for every document, given to getDocument as `worker`. Not as GlobalWorkerOptions.workerPort:
            // then each loading task owns the worker and destroys it with its document, and the next LOAD_PDF, which
            // does not await that destroy (emptyViewer), throws "PDFWorker.create - the worker is being destroyed"
            worker: new pdfjsLib.PDFWorker({ port }),
            // No URL to fetch from offline: the worker asks this page for the wasm, and the factory answers with the
            // inlined bytes. pdf.js 5.3 then uses neither the CMYK profile nor qcms
            WasmFactory: class extends InlineWasmFactory {
                constructor() {
                    super(wasmFiles);
                }
            },
            useWorkerFetch: false,
        };
    } else {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.mjs`;
    }

    if (TEST_MODE) {
        console.log('Viewer running in test mode');
        testUsingFile();
    } else {
        initHostCommunication();
    }

    eventBus = new pdfjsViewer.EventBus();

    addEventListeners();

    initPdfjs().then(() => {
        debug('docavea-viewer initialized.');
        if (viewerMode !== 'viewerTab') {
            setInitialTheme();
            sendResponse(ResponseType.VERSION, viewerVersion);
        }
    });
}

function addEventListeners() {
    // listen for the pagesinit message from pdfjs. pdfjs must not be call until the pagesinit flag has been set
    // document.addEventListener('pagesinit', () => pagesinitHandler());
    eventBus.on('pagesinit', () => pagesinitHandler());

    // Add page change listener for viewerTab mode
    if (viewerMode === 'viewerTab') {
        eventBus.on('pagechanging', () => {
            updatePageDisplay();
        });
        return; // don't add other event listeners if we are in viewerTab mode
    }

    document.addEventListener('drop', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (fileDropDisabled) {
            return;
        }
        if (event.dataTransfer) {
            const files = event.dataTransfer.files;
            sendResponse(ResponseType.FILES_DROPPED, files);
        } else {
            console.error('drop event.dataTransfer == null');
        }
    });
    document.addEventListener('dragleave', (event) => {
        event.preventDefault();
        event.stopPropagation();
    });
    document.addEventListener('dragover', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (fileDropDisabled && event.dataTransfer) {
            event.dataTransfer.dropEffect = 'none';
        }
    });
    // document.addEventListener('contextmenu', (event) => {
    //     event.preventDefault();
    //     // Clear any text selection to make sure we are syncronised with the copy button
    //     const selection = window.getSelection() as Selection;
    //     selection.removeAllRanges();
    // });
    // keyboard handeling
    let altGrKeyPressed = false; // used to save AltGr state
    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'AltGraph') {
            altGrKeyPressed = true;
        }
        
        // Keyboard shortcuts for viewerTab mode
        if (viewerMode === 'viewerTab' && pagesinit) {
            const ctrlOrCmdKey = Utils.isMacOs() ? event.metaKey : event.ctrlKey;
            if (ctrlOrCmdKey && (event.key === 'f' || event.key === 'F')) {
                event.preventDefault();
                openFindBar();
                return;
            }
            if (event.key === 'Escape' && findBarOpen) {
                event.preventDefault();
                closeFindBar();
                return;
            }
            // Don't run single-key shortcuts while typing in an input field (find bar, page input)
            const target = event.target as HTMLElement;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                return;
            }
            if (event.key === '+' || event.key === '=') {
                event.preventDefault();
                handleZoomClick(-1); // Zoom in
            } else if (event.key === '-') {
                event.preventDefault();
                handleZoomClick(1); // Zoom out
            } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
                event.preventDefault();
                handlePrevPage();
            } else if (event.key === 'ArrowRight' || event.key === 'PageDown') {
                event.preventDefault();
                handleNextPage();
            } else if (event.key === 'Home') {
                event.preventDefault();
                setCurrentPage(1);
                viewChanged(false, false);
            } else if (event.key === 'End') {
                event.preventDefault();
                if (pdfDocument) {
                    setCurrentPage(pdfDocument.numPages);
                    viewChanged(false, false);
                }
            }
        }
    });
    document.addEventListener('keyup', (event: KeyboardEvent) => {
        if (event.key === 'AltGraph') {
            altGrKeyPressed = false;
        }
        const keyCode = event.code;
        const ctrlKey = Utils.isMacOs() ? event.metaKey : event.ctrlKey;
        const altKey = event.altKey || altGrKeyPressed;
        const realCtrlKey = event.ctrlKey;

        const handled = keyScroll(keyCode);
        if (handled) {
            return;
        }
        if (keyCode !== 'Shift' && keyCode !== 'Control' && keyCode !== 'Meta' && keyCode !== 'Alt' && keyCode !== 'AltGraph') {
            if (altKey && realCtrlKey && keyCode === 'Digit0') {
                debugOn = !debugOn; // toggle debug information
                console.log(`${Utils.getFormatedTimestamp()} viewer log output`, (debugOn ? 'on' : 'off'));
            } else {
                sendResponse(ResponseType.KEYUP, [keyCode, event.shiftKey, ctrlKey, altKey, realCtrlKey]);
            }
        }
    });
}

function initHostCommunication() {
    const host = resolveHost(document.referrer, window.location.href, OFFLINE);
    if (!host) {
        return;
    }
    const baseUrl = host.targetOrigin;
    viewerId = host.id;

    // Check if ID is > DOCAVEA_VIEWERS_IN_TABS_OFFSET so we know if we are in viewerTab mode
    if (viewerId >= DOCAVEA_VIEWERS_IN_TABS_OFFSET) {
        window.opener?.postMessage({ type: 'ready', viewerId }, baseUrl);
        window.addEventListener('message', (event) => {
            debug('Received message in viewerTab mode:', event.data);
            
            if (event.origin === baseUrl) {
                if (event.data.type === 'init') {
                    viewerMode = 'viewerTab';
                    toolbarHeight = 44; // default toolbar height for viewerTab mode
                    loadPdfPayload = event.data.data.loadPdfPayload;
                    debug('loadPdfPayload', loadPdfPayload);
                    
                    viewerDocName = event.data.data.docName;

                    setThemeWithToolbar(event.data.data.viewerThemeBackground);
                    // Set the background color of the viewer container
                    const container = document.getElementById('viewerContainer');
                    if (container) {
                        container.style.background = event.data.data.viewerThemeBackground[9];
                    }
                    loadPdf().then(() => {
                        initResizeEvents();
                    });
                } else if (event.data.type === 'viewerThemeBackgrounds') {
                    setThemeWithToolbar(event.data.data.viewerThemeBackground);
                    // Set the background color of the viewer container
                    const container = document.getElementById('viewerContainer');
                    if (container) {
                        container.style.background = event.data.data.viewerThemeBackground[9];
                    }
                } else if (event.data.type === 'setRotations') {
                    // note we reload the PDF to change orientations
                    pagesinit = false;
                    pageNumberBeforeRotate = pdfViewer.currentPageNumber;
                    console.log('setRotations', event.data);
                    console.log('setRotations', event.data.data.pageOrientations);

                    loadPdfPayload.orientations = _.clone(event.data.data.pageOrientations);
                    loadPdf();
                } else {
                    console.warn('Docavea viewer unknown message type', event.data.type);
                }
            }
        });
        return;
    }

    // Setup channel messaging to the host
    const channel = new MessageChannel();
    channel.port1.onmessage = (event: MessageEvent) => {
        onRequest(event);
    };
    initChannelMessaging(channel.port1);

    const message = `docaveaviewer-is-alive ID=${viewerId}`;
    // send an old fashioned message to the host so it know we are ready and and give it a port to talk to us
    window.parent.postMessage(message, baseUrl, [channel.port2]);
    setViewerId(viewerId);
}

function testUsingFile() {
    debug('testUsingFile');
    document.body.style.backgroundColor = 'lightgray';
    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        viewerContainer.style.backgroundColor = 'lightgray';
    }

    // Not in the offline file, which may name no Firebase project (scripts/inline-offline.js refuses one)
    const fileUrl = OFFLINE ? '' :
        'https://firebasestorage.googleapis.com/v0/b/docavea3.appspot.com/o/PDFJS%20test%20document.pdf?alt=media&token=4305a36b-6b19-4b65-9609-2be46d7415eb';
    // 'https://firebasestorage.googleapis.com/v0/b/docavea3.appspot.com/o/zoom%20test.pdf?alt=media&token=595e8de6-c153-4eb1-a313-0800d02dea1b';
    // 'https://firebasestorage.googleapis.com/v0/b/docavea3.appspot.com/o/Scan.pdf?alt=media&token=9764808d-cbc2-4b88-9993-60e1c57c8e30';
    const fileSize = 426363;
    // const fileSize = 1441456;
    const scalePayload = {
        scale: 1,
        zoom: Zoom.PAGE,
        dualPage: DualPage.SINGLE,
    };
    loadPdfPayload = {
        docKey: '',
        srcType: SrcType.URL,
        src: fileUrl,
        fileSize,
        pageNumber: 1,
        scalePayload,
        centerX: 0,
        centerY: 0,
        orientations: {},
        minScale: 0.25,
        maxScale: 4,
        password: '',
        throttleEventsTime: 0,
    };

    pagesinit = false;
    pageNumberBeforeRotate = 0;     // On initial load we don't change page number in pagesinitHandler to signal that we set it to 0

    loadPdf();
    // Note: pageInfos are send from pagesinit handler
}

async function initPdfjs() {
    // initialize localization service, so time stamp in embedded comments are shown correctly
    // l10n = new (pdfjsViewer as any).GenericL10n('en-US');
    // const dir = await l10n.getDirection();
    // document.getElementsByTagName('html')[0].dir = dir;

    // Enable hyperlinks within PDF files.
    pdfLinkService = new pdfjsViewer.PDFLinkService({
        eventBus,
    });

    // Enable find controller.
    pdfFindController = new (pdfjsViewer as any).PDFFindController({
        eventBus,
        linkService: pdfLinkService,
    });

    document.head.append(redactionModeStyle);
    createPdfViewer(false);
}

// The annotation mode is fixed when a PDFViewer is made, so switching redaction mode makes a new one (visiodocs-app#492).
// Redaction mode draws form fields into the page (ENABLE_STORAGE), as the redact-pdf service paints them; otherwise
// they are HTML inputs on top (ENABLE_FORMS, pdf.js' default).
function createPdfViewer(redactionMode: boolean) {
    const container = document.getElementById('viewerContainer');
    if (container) {
        pdfViewerAbort?.abort();    // the old viewer's resize observer and scroll listener
        pdfViewerAbort = new AbortController();
        pdfViewerRedactionMode = redactionMode;
        // Initialize PDFViewer
        pdfViewer = new pdfjsViewer.PDFViewer({
            container,
            eventBus,
            removePageBorders: true,
            linkService: pdfLinkService,
            findController: pdfFindController,
            annotationMode: redactionMode ? pdfjsLib.AnnotationMode.ENABLE_STORAGE : pdfjsLib.AnnotationMode.ENABLE_FORMS,
            abortSignal: pdfViewerAbort.signal,
            // l10n,
        });
        // pdfViewer.textLayerMode = Utils.enableTextSelection() ? TextLayerMode.ENABLE : TextLayerMode.DISABLE;
        pdfViewer.textLayerMode = TextLayerMode.ENABLE_ENHANCE;

        // See https://github.com/mozilla/pdf.js/issues/11245
        if (Utils.isIos()) {
            pdfViewer.maxCanvasPixels = 4000 * 4000;
        }

        pdfLinkService.setViewer(pdfViewer);
    } else {
        console.error(`getElementById('viewerContainer') failed`);
    }
}

function initTextSelection() {
    let selectStartX: number;
    let selectStartY: number;
    let selectStart: number;

    document.addEventListener('mousedown', (event: MouseEvent) => {
        selectStartX = event.clientX;
        selectStartY = event.clientY;
    });

    document.addEventListener('touchstart', (event: TouchEvent) => {
        event.preventDefault();

        selectStart = event.timeStamp;
        selectStartX = event.touches[0].clientX;
        selectStartY = event.touches[0].clientY;
    });

    document.addEventListener('mouseup', async (event: MouseEvent) => {
        if (!pagesinit) {
            return;
        }

        if (Math.abs(event.clientX - selectStartX) > 5 || Math.abs(event.clientY - selectStartY) > 5) {
            selectEnd(event.clientX, event.clientY);
        } else {
            const payload: ClickEvent = {
                x: selectStartX,
                y: selectStartY,
            };
            sendResponse(ResponseType.CLICK, payload);
        }
    });

    document.addEventListener('touchend', async (event: TouchEvent) => {
        event.preventDefault();
        if (!pagesinit) {
            return;
        }

        if (event.timeStamp - selectStart > 200) {
            selectEnd(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
        } else {
            sendResponse(ResponseType.SHOW_TEXT_COPY_BUTTON, false);
            const selection = window.getSelection() as Selection;
            selection.removeAllRanges();
        }
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const selectEnd = async (x: number, y: number) => {
        // mouse drag (mark text)
        if (!textCopyModeEnabled) {
            // const nodeInfos = iterateNodes();
            // await viewChanged(false, false, { nodeInfos });
            const { selectionRectangels: selectionRectangles, selectionText } = selectionHandler(true);
            if (selectionRectangles.length > 0) {
                removeBadRectangles(selectionRectangles, selectStartX, selectStartY, x, y);
                await viewChanged(false, false, { selectionRectangles, selectionText });
            }
        } else {
            const selection = window.getSelection() as Selection;
            if (!selection.isCollapsed) {
                // we have a non-zero length selection
                sendResponse(ResponseType.SHOW_TEXT_COPY_BUTTON, true);
            }
        }
    };
}

// Forward throttled mouse positions (same client space as ResponseType.CLICK) so the parent can
// hover-preview pending redaction marks — its markup canvas is pointer-events:none while text
// selection is possible, so it cannot see the mouse itself. x/y=-1 on leave clears the preview.
document.addEventListener('mousemove', _.throttle((event: MouseEvent) => {
    if (!pagesinit) {
        return;
    }
    const payload: ClickEvent = { x: event.clientX, y: event.clientY };
    sendResponse(ResponseType.MOUSE_MOVE, payload);
}, 50));

document.documentElement.addEventListener('mouseleave', () => {
    if (!pagesinit) {
        return;
    }
    const payload: ClickEvent = { x: -1, y: -1 };
    sendResponse(ResponseType.MOUSE_MOVE, payload);
});

document.addEventListener('copy', async () => {
    doCopyText();
});

async function doCopyText() {
    const { selectionText } = selectionHandler(true);
    // Unblocking clipboard access https://web.dev/async-clipboard/
    await navigator.clipboard.writeText(selectionText);
    sendResponse(ResponseType.SHOW_TEXT_COPY_BUTTON, false);
}

async function pagesinitHandler() {
    // We can use pdf-viewer now
    pagesinit = true;
    // console.log('pagesinitHandler()');

    initMouseWheelZoom(); // done here so calculateCurrentScale() will work

    setOrientations(loadPdfPayload.orientations);
    await scaleHandler();

    // document.addEventListener('keydown', (event: KeyboardEvent) => {
    //     if (event.key === 'w') {
    //         const target = document.getElementById('viewerContainer');

    //         if (target) {
    //             target.dispatchEvent(new MouseEvent(event.type, event));
    //         }
    //     }
    // });

    await pdfDocument.getPage(pdfViewer.currentPageNumber); // using getPage() as a way to ensure everything is ready.
    if (pageNumberBeforeRotate === 0) {
        pdfViewer.currentPageNumber = loadPdfPayload.pageNumber; // initial load so we load the requested page
        setScrollOffsets();
        // send first view changed event
        await viewChanged(true, false);
        pdfLoading = false;

        // initialize stuff if it is initial load
        initScrollEvents();
        initResizeEvents();
        initMouseWheelZoomEvents();
        initPinchZoom();
        initTextSelection();
    } else if (viewerMode === 'viewerTab') {
        // Ensure mouse wheel zoom is available in tab viewer mode
        initMouseWheelZoomEvents();
        initPinchZoom();

        // Update page navigation display after orientation change
        updatePageDisplay();
        const pageLabel = document.getElementById('pageLabel');
        if (pageLabel && pdfDocument) {
            pageLabel.textContent = `of ${pdfDocument.numPages}`;
        }
    }
    // this awkward construction is made because eventBus.off both needs the event name and the function name
    const pagesLoaded = () => {
        eventBus.off('pagesloaded', pagesLoaded);
        if (pageNumberBeforeRotate > 0) {
            pdfViewer.scrollPageIntoView({ pageNumber: pageNumberBeforeRotate });    // orientations load so we try to keep current page
        }
        sendResponse(ResponseType.PDF_LOADED, {});
        // make sure we end loading by sending a 100% loaded message
        sendResponse(ResponseType.PROGRESS, 100);
        viewChanged(false, false, { pageNumberBeforeRotate });
    };
    eventBus.on('pagesloaded', pagesLoaded);

    debug('pagesinit event received, pdf viewer ready');
}

function initResizeEvents() {
    window.addEventListener('resize', _.throttle(async () => {
        await scaleHandler();
        viewChanged(false, false);
    }, 100));
}

function initScrollEvents() {
    const container = document.getElementById('viewerContainer');
    if (container) {
        let scrollTop = container.scrollTop;
        let scrollLeft = container.scrollLeft;
        container.addEventListener('scroll', async () => {
            // scroll event happen a lot, even on non scroll events so we check that scollTop actually changed
            if ((scrollTop !== container.scrollTop || scrollLeft !== container.scrollLeft) && pagesinit && !pdfLoading) {
                scrollTop = container.scrollTop;
                scrollLeft = container.scrollLeft;
                await viewChanged(false, true);
            }
        });
    } else {
        console.error(`getElementById('viewerContainer') failed`);
    }
}

async function onRequest(event: MessageEvent) {
    if (viewerMode === 'viewerTab') {
        return; // don't handle requests if we are in viewerTab mode
    }
    if (event.data === undefined) {
        console.error('event.data is undefined');
        return;
    }

    const request: ViewerRequest = event.data;
    latestRequest = RequestType[request.requestType];

    if (pdfLoading && request.requestType !== RequestType.THEME_BACKGROUNDS) {
        debug('Requests received before PDF has finished loading is ignored. request:', RequestType[request.requestType]);
        // debugger;
        sendResponse(ResponseType.REJECT, 'PDF is loading');
        return;
    }
    debug('request.requestType', RequestType[request.requestType], 'payload', request.payload);
    switch (request.requestType) {
        case RequestType.SCALE:
            oldZoom = loadPdfPayload.scalePayload.zoom;
            loadPdfPayload.scalePayload = _.clone(request.payload);

            if (await scaleHandler()) {
                await viewChanged(false, false);
            }
            break;

        case RequestType.LOAD_PDF:
            // console.log('RequestType.LOAD_PDF', request.payload);
            pagesinit = false;
            pdfLoading = true;
            if (searchTimeout !== undefined) {
                clearTimeout(searchTimeout);
                searchTimeout = undefined;
            }
            // Deep clone payload, as it can be overwritten by a new command, to global variable
            loadPdfPayload = _.cloneDeep(request.payload);
            fileDropDisabled = request.payload?.fileDropDisabled === true;
            pageNumberBeforeRotate = 0;     // On initial load we don't change page number in pagesinitHandler to signal that we set it to 0
            loadPdfPayload.scalePayload = _.clone(request.payload.scalePayload);
            loadPdfPayload.scalePayload.scale = _.clamp(
                loadPdfPayload.scalePayload.scale,
                loadPdfPayload.minScale,
                loadPdfPayload.maxScale);

            loadPdf();
            // Note: pageInfos are send from pagesinit handler
            break;

        case RequestType.SET_CURRENT_PAGE:
            // eslint-disable-next-line no-case-declarations
            const pageNumberChanged = setCurrentPage(request.payload);
            if (pageNumberChanged) {
                await viewChanged(false, false);
            }
            break;

        case RequestType.MAKE_DOC_THUMBNAIL:
            makeDocThumbnail(request.payload, pdfDocument);
            break;

        case RequestType.FIND:
            find(request.payload);
            break;

        case RequestType.SET_ORIENTATIONS:
            // note we reload the PDF to change orientations
            pagesinit = false;
            pageNumberBeforeRotate = pdfViewer.currentPageNumber;
            loadPdfPayload.orientations = _.clone(request.payload);
            loadPdf();
            break;

        case RequestType.WHEEL_EVENT:
            // eslint-disable-next-line no-case-declarations
            const payload: WheelPayload = request.payload;
            if (payload.zoom) {
                wheelZoom(payload.deltaY, payload.zoomPointX, payload.zoomPointY);
            } else {
                const viewer = document.getElementById('viewerContainer');
                if (viewer) {
                    viewer.scrollBy(0, payload.deltaY);
                } else {
                    console.error(`getElementById('viewerContainer') failed`);
                }
            }
            break;

        case RequestType.THEME_BACKGROUNDS:
            setTheme(request.payload);
            break;

        case RequestType.LOAD_AND_THUMB:
            loadAndThumb = true;
            if (searchTimeout !== undefined) {
                clearTimeout(searchTimeout);
            }
            loadAndThumbPayload = request.payload;
            loadPdfPayload = {
                docKey: loadAndThumbPayload.docKey,
                srcType: loadAndThumbPayload.srcType,
                src: loadAndThumbPayload.src,
                fileSize: 0,
                pageNumber: loadAndThumbPayload.pageNo,
                scalePayload: {
                    scale: 1,
                    zoom: Zoom.PAGE,
                    dualPage: DualPage.SINGLE,
                },
                centerX: 0,
                centerY: 0,
                orientations: {},
                minScale: 0,
                maxScale: 0,
                password: loadAndThumbPayload.password,
                throttleEventsTime: 0,
            };
            pagesinit = false;
            loadPdf();

            break;

        case RequestType.MAKE_QUICKVIEW:
            makeQuickView(request.payload, pdfDocument);
            break;

        case RequestType.KEY_EVENT:
            keyScroll(request.payload);
            break;

        case RequestType.TEXT_COPY_MODE_ENABLED:
            textCopyModeEnabled = request.payload;
            if (!textCopyModeEnabled) {
                sendResponse(ResponseType.SHOW_TEXT_COPY_BUTTON, false);
            }
            break;

        case RequestType.COPY_SELECTION_TO_CLIPBOARD:
            window.focus();
            doCopyText();
            break;

        case RequestType.CAN_SELECT_TEXT:
            // eslint-disable-next-line no-case-declarations
            const _payload = canSelectText(request.payload.pageNumber);
            sendResponse(ResponseType.CAN_SELECT_TEXT, { payload: _payload });
            break;

        case RequestType.EMPTY_VIEWER:
            emptyViewer();
            break;

        case RequestType.SET_SPLIT_MARKS:
            setSplitMarks(request.payload);
            break;

        case RequestType.SET_DELETE_MARKS:
            setDeleteMarks(request.payload);
            break;

        case RequestType.FIND_MATCH_RECTS:
            // eslint-disable-next-line no-case-declarations
            const findMatchRectsPayload: FindMatchRectsPayload = request.payload;
            findMatchRects(pdfDocument, findMatchRectsPayload.query, findMatchRectsPayload.pattern, findMatchRectsPayload.keepPlacesApart === true)
                .then((matchRectsResponse) => sendResponse(ResponseType.MATCH_RECTS, matchRectsResponse))
                .catch((err) => {
                    console.error('FIND_MATCH_RECTS failed', err);
                    sendResponse(ResponseType.MATCH_RECTS, { query: findMatchRectsPayload.query, pattern: findMatchRectsPayload.pattern, matches: [], pagesTotal: 0, pagesFailed: -1 });
                });
            break;

        case RequestType.GET_PAGE_RECTS:
            // eslint-disable-next-line no-case-declarations
            const getPageRectsPayload: GetPageRectsPayload = request.payload;
            getPageRects(pdfDocument, getPageRectsPayload.pages)
                .then((pageRectsResponse) => sendResponse(ResponseType.PAGE_RECTS, pageRectsResponse))
                .catch((err) => {
                    console.error('GET_PAGE_RECTS failed', err);
                    sendResponse(ResponseType.PAGE_RECTS, { requested: getPageRectsPayload.pages, pages: [], pagesFailed: -1 });
                });
            break;

        case RequestType.SET_REDACTION_MODE:
            // Reload, keeping the page, as SET_ORIENTATIONS does: the PDFViewer and the hidden marks are set at load
            if (!!request.payload?.enabled !== (loadPdfPayload.redactionMode === true)) {
                loadPdfPayload.redactionMode = !!request.payload?.enabled;
                pagesinit = false;
                pageNumberBeforeRotate = pdfViewer.currentPageNumber;
                loadPdf();
            }
            break;

        case RequestType.COUNT_PDF_LOSSES:
            countPdfLosses();
            break;

        case RequestType.GET_IMAGE_RECTS:
            // eslint-disable-next-line no-case-declarations
            const getImageRectsPayload: GetImageRectsPayload = request.payload;
            getImageRects(pdfDocument, getImageRectsPayload.pageNumber, pdfjsLib.OPS)
                .then((imageRectsResponse) => sendResponse(ResponseType.IMAGE_RECTS, imageRectsResponse))
                .catch((err) => {
                    console.error('GET_IMAGE_RECTS failed', err);
                    sendResponse(ResponseType.IMAGE_RECTS, { pageNumber: getImageRectsPayload.pageNumber, rects: [], failed: true });
                });
            break;

        default:
            console.error('Unknown pdfViewerRequest', request);
            break;
    }
}

export async function loadPdf() {
    if (pagesinit) {
        debugger;
    }

    // Show progress indicator for viewerTab mode
    if (viewerMode === 'viewerTab') {
        showProgress();
    }

    // Clear screen if the viewer already have an open document
    emptyViewer();

    // Construct the load parameters struct. Note: url and data must not be set to anything if not used or pdfjs will crash
    const loadParams: any = {
        cMapUrl: '../../node_modules/pdfjs-dist/cmaps/',    // Some PDFs need external cmaps (character maps).
        cMapPacked: true,
        password: loadPdfPayload.password,
        ...(OFFLINE ? offlineLoadParams : {  // offline: worker and inlined wasm (viewerMain), no wasmUrl or iccUrl
            wasmUrl: '/wasm/',    // Path to WASM files directory for OpenJPEG and QCMS support (PDF.js v5+)
            iccUrl: '/iccs/',     // Path to ICC profiles for better CMYK to RGB color conversion (PDF.js v5+)
        }),
    };
    if (loadPdfPayload.srcType === SrcType.URL) {
        loadParams.url = loadPdfPayload.src;
    } else {
        loadParams.data = loadPdfPayload.src as ArrayBuffer;
    }

    // Load the document.
    const loadingTask = pdfjsLib.getDocument(loadParams);
    // setup password missing callback
    let loadingTaskDestroyed = false;
    loadingTask.onPassword = () => {
        debug('password missing');
        loadingTaskDestroyed = true;
        if (viewerMode === 'viewerTab') {
            hideProgress();
        } else {
            sendResponse(ResponseType.PASSWORD_PROTECTED, '');
        }
        loadingTask.destroy();
        pdfLoading = false;
    };
    // Setup progress reporting
    let throttleTime = 0;
    loadingTask.onProgress = (progress) => {
        if (Date.now() - throttleTime > 1000) {  // only send events every 1 sec. as fast event can overload chromium on some PC's
            throttleTime = Date.now();

            // Calculate percentage - prefer progress.total over fileSize for accuracy
            let percentage = 0;
            const total = progress.total || loadPdfPayload.fileSize;

            if (total > 0 && progress.loaded > 0) {
                percentage = (progress.loaded / total) * 100;

                if (viewerMode === 'viewerTab') {
                    updateProgress(percentage);
                    debug(`Progress: ${Math.round(percentage)}% (${progress.loaded}/${total} bytes)`);
                } else {
                    // occasionally loadPdfPayload.fileSize and progress.loaded can become zero - don't report those as it gives progress bar flicker
                    if (loadPdfPayload.fileSize > 0) {
                        sendResponse(ResponseType.PROGRESS, percentage);
                    }
                }
            }
        }
    };
    // wait for loading task to finish
    loadingTask.promise.then(async (_pdfDocument) => {
        if (viewerMode === 'viewerTab') {
            updateProgress(100);
            // Keep progress visible briefly before hiding
            setTimeout(() => hideProgress(), 300);
        } else {
            sendResponse(ResponseType.PROGRESS, 100);
        }
        pdfDocument = _pdfDocument;

        if (!loadAndThumb) {
            // Redaction v2 (visiodocs-app#492): hide the marks the redacted copy leaves out before the first page is drawn
            const redactionMode = loadPdfPayload.redactionMode === true && viewerMode !== 'viewerTab';
            redactionModeStyle.textContent = '';
            if (redactionMode) {
                try {
                    await hideRemovedMarks(pdfDocument, redactionModeStyle);
                } catch (error) {
                    // Fail closed: shown with its marks, the page would not be the page the service paints
                    console.error('Redaction mode could not read the PDF marks', error);
                    if (pdfDocument === _pdfDocument) {
                        pdfLoading = false;
                        sendResponse(ResponseType.PDF_LOAD_FAILED, {});
                    }
                    return;
                }
                if (pdfDocument !== _pdfDocument) return;   // another load started meanwhile
            }
            if (redactionMode !== pdfViewerRedactionMode) {
                createPdfViewer(redactionMode);
            }

            // Document loaded, specifying document for the viewer and the linkService.
            // Set the linkService document first to avoid annotation timing issues
            pdfLinkService.setDocument(pdfDocument, null);
            pdfViewer.setDocument(pdfDocument);

            // Fetch outline/bookmarks for viewerTab mode
            if (viewerMode === 'viewerTab') {
                pdfDocument.getOutline().then((outline: any) => {
                    pdfOutline = outline;
                    renderOutline();
                }).catch((error: any) => {
                    console.warn('Failed to get PDF outline:', error);
                    pdfOutline = null;
                    renderOutline();
                });
            }
        } else {
            const payload: ThumbnailPayload = {
                width: loadAndThumbPayload.width,
                height: loadAndThumbPayload.height,
                pageNumber: loadAndThumbPayload.pageNo,
                orientation: 0,
                mimeType: 'image/jpeg',
                jpegQuality: 0.72,
            };
            await pdfDocument.getPage(1);
            await makeLoadThumbnail(payload, pdfDocument);

            loadAndThumb = false;
        }
    }, (exception) => {
        if (loadingTaskDestroyed !== true) {
            console.error('pdf getDocument failed', exception?.message);
            if (viewerMode === 'viewerTab') {
                hideProgress();
            } else {
                sendResponse(ResponseType.PDF_LOAD_FAILED, {});
            }
        }
    });
}

// Redaction v2 (visiodocs-app#492): what a redacted copy of the loaded document loses, for the app's notice
async function countPdfLosses() {
    const docAtStart = pdfDocument;
    if (!docAtStart) {
        // Between documents, e.g. during SET_REDACTION_MODE's reload, which (like SET_ORIENTATIONS) does not set pdfLoading.
        // The app asks again at PDF_LOADED.
        sendResponse(ResponseType.REJECT, 'PDF is loading');
        return;
    }
    let losses: PdfLosses;
    try {
        losses = (await cachedScan(docAtStart)).losses;
    } catch (error) {
        console.error('COUNT_PDF_LOSSES failed', error);
        losses = { links: 0, notes: 0, formFields: 0, bookmarks: 0, attachments: 0, freeTextBoxes: 0, mediaPlayers: 0, failed: true };
    }
    if (pdfDocument === docAtStart) {
        sendResponse(ResponseType.PDF_LOSSES, losses);
    }
}

const emptyViewer = () => {
    // Clear screen if the viewer already have an open document
    if (pdfDocument) {
        pdfDocument.cleanup();
        pdfDocument.destroy();
        pdfDocument = null;
        pdfViewer.setDocument(null);
        pdfLinkService.setDocument(null);
    }
    clearDivs();

    // Clear bookmark sidebar for viewerTab mode
    if (viewerMode === 'viewerTab') {
        // Reset find state (the find controller starts over on the reloaded document)
        lastFindQuery = '';
        if (findMatchLabel) {
            findMatchLabel.textContent = '';
        }
        pdfOutline = null;
        if (bookmarkContent) {
            bookmarkContent.innerHTML = '';
        }
        // Close sidebar if it was open
        if (sidebarOpen && bookmarkSidebar) {
            sidebarOpen = false;
            bookmarkSidebar.style.width = '0px';
            const viewerContainer = document.getElementById('viewerContainer');
            if (viewerContainer) {
                viewerContainer.style.left = '0';
                viewerContainer.style.width = '100%';
            }
        }
    }
};

function find(payload: FindPayload) {
    if (!pagesinit) {
        return;
    }
    // console.log('find', payload);
    eventBus.dispatch('find', {
        type: payload.findCommand,
        query: payload.findParms.query,
        highlightAll: payload.findParms.highlightAll,
        caseSensitive: payload.findParms.caseSensitive,
        entireWord: payload.findParms.entireWord,
        findPrevious: payload.findParms.findPrevious,
    });

    eventBus._on('updatefindmatchescount', ({ matchesCount }) => {
        // console.log('updatefindmatchescount', matchesCount);

        const response: FindResponse = {
            currentMatch: matchesCount.current,
            totalMatches: matchesCount.total,
        };
        sendResponse(ResponseType.FIND_UPDATE, response);
        setTimeout(() => {
            setFindHighlights();
        }, 100);
    });

    // eventBus._on('updatefindcontrolstate', ({ state }) => {
    //     console.log('updatefindcontrolstate', state);
    // });
}

export async function scaleHandler() {
    if (!pagesinit) {
        return false;
    }
    // debug('scale', scalePayload);

    switch (loadPdfPayload.scalePayload.dualPage) {
        case DualPage.SINGLE:
            pdfViewer.spreadMode = SpreadMode.NONE;
            break;
        case DualPage.DUAL:
            pdfViewer.spreadMode = SpreadMode.ODD;
            break;
        case DualPage.DUAL_COVER:
            pdfViewer.spreadMode = SpreadMode.EVEN;
            break;
    }

    const container = document.getElementById('viewerContainer');
    if (container) {
        const page = await pdfReady();
        if (page < 0) {
            return;
        }
        switch (loadPdfPayload.scalePayload.zoom) {
            case Zoom.SCALE:
                {
                    let newScale: number;
                    if (loadPdfPayload.scalePayload.scale >= 0) {
                        newScale = loadPdfPayload.scalePayload.scale;
                    } else {
                        const scaleFactor = -loadPdfPayload.scalePayload.scale;
                        const currentScale = calculateCurrentScale(page, oldZoom);
                        newScale = currentScale * scaleFactor;
                    }
                    if ((newScale >= loadPdfPayload.minScale) && (newScale <= loadPdfPayload.maxScale)) {
                        pdfViewer.currentScaleValue = newScale;
                    } else {
                        return false;
                    }
                    pdfViewer.currentScaleValue = newScale;
                }
                break;
            case Zoom.PAGE:
                // currentScaleValue accepted values: 'auto', 'page-actual', 'page-width', 'page-height', 'page-fit', or a zoom level in percents.
                pdfViewer.currentScaleValue = calculateCurrentScale(page, Zoom.PAGE);
                break;
            case Zoom.WIDTH:
                pdfViewer.currentScaleValue = calculateCurrentScale(page, Zoom.WIDTH);
                break;
        }
    } else {
        console.error(`getElementById('viewerContainer') failed`);
    }

    // debug('scale', pdfViewer._currentScaleValue);

    // Update view to refresh page borders and other UI elements
    await viewChanged(false, false);

    return true;
}

export async function setOrientations(orientations: { [pageNumber: number]: number }) {
    const base = orientations[0] || 0;
    for (let pageNo = 0; pageNo < pdfDocument.numPages; pageNo++) {
        pdfViewer._pages[pageNo].rotation = base + (orientations[pageNo + 1] || 0);
    }
}

function setScrollOffsets() {
    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        const scrollLeft = Math.max(loadPdfPayload.centerX - viewerContainer.clientWidth / 2, 0);
        const scrollTop = Math.max(loadPdfPayload.centerY - viewerContainer.clientHeight / 2, 0);
        viewerContainer.scrollTo(scrollLeft, scrollTop);
    } else {
        console.error(`document.getElementById('viewerContainer') failed`);
    }
}

function keyScroll(keyCode: string) {
    let handled = true;
    const container = document.getElementById('viewerContainer');
    if (container) {
        const oldLeft = container.scrollLeft;
        const oldTop = container.scrollTop;
        switch (keyCode) {
            case 'ArrowRight':
                container.scrollLeft += 100;
                break;
            case 'ArrowLeft':
                container.scrollLeft -= 100;
                break;
            case 'ArrowUp':
                container.scrollTop -= 100;
                break;
            case 'ArrowDown':
                container.scrollTop += 100;
                break;
            case 'PageUp':
                setCurrentPage(pdfViewer.currentPageNumber - 1);
                break;
            case 'PageDown':
                setCurrentPage(pdfViewer.currentPageNumber + 1);
                break;
            case 'Home':
                setCurrentPage(1);
                break;
            case 'End':
                setCurrentPage(pdfDocument.numPages);
                break;
            default:
                handled = false;
                break;
        }
        if (container.scrollLeft !== oldLeft || container.scrollTop !== oldTop) {
            viewChanged(true, false);
        }
    } else {
        console.error(`getElementById('viewerContainer') failed`);
    }

    return handled;
}

function setCurrentPage(pageNumber: number) {
    if (pageNumber > 0 && pageNumber <= pdfDocument.numPages && pdfViewer.currentPageNumber !== pageNumber) {
        loadPdfPayload.pageNumber = pageNumber;
        pdfViewer.currentPageNumber = pageNumber;
        pdfViewer.scrollPageIntoView({ pageNumber });
        return true;
    }
    return false;
}

function setTheme(theme: string[]) {
    // console.log('setTheme called with theme[10]:', theme[10], 'full theme:', theme);
    currentTheme = theme; // Store theme globally for outline rendering
    selectedPageBorder = theme[8];

    const viewerContainer = document.getElementById('theme');
    if (viewerContainer) {
        if (Utils.isWindows()) {
            viewerContainer.innerHTML = `
                ::-webkit-scrollbar { width: 10px; height: 10px; }
                ::-webkit-scrollbar-button { width: 0px; height: 0px; }
                ::-webkit-scrollbar-thumb { background: ${theme[1]}; border: 0px; border-radius: 5px; }
                ::-webkit-scrollbar-thumb:hover { background: ${theme[2]}; }
                ::-webkit-scrollbar-thumb:active { background: ${theme[3]}; }
                ::-webkit-scrollbar-track { background: none; border: 0px; border-radius: 5px; }
                ::-webkit-scrollbar-track:hover { background: ${theme[5]}; }
                ::-webkit-scrollbar-track:active { background: ${theme[6]}; }
                ::-webkit-scrollbar-corner { background: ${theme[7]}; }
                .container { -webkit-touch-callout: none; } 
                body { background-color: ${theme[9]} !important; }
                #viewerContainer { background-color: ${theme[9]} !important; }
            `;
        } else if (Utils.isMacOs()) {
            viewerContainer.innerHTML = `
                ::-webkit-scrollbar { width: 10px; height: 10px; }
                ::-webkit-scrollbar-thumb { background: ${theme[1]}; border-radius: 5px; }
                .container { -webkit-touch-callout: none; }
                body { background-color: ${theme[9]} !important; }
                #viewerContainer { background-color: ${theme[9]} !important; }
            `;
        } else {
            viewerContainer.innerHTML = `
                .container { -webkit-touch-callout: none; }
                body { background-color: ${theme[9]} !important; }
                #viewerContainer { background-color: ${theme[9]} !important; }
        `;
        }
        const toolbar = document.getElementById('viewerTabToolbar');
        if (viewerMode === 'viewerTab' && toolbar) {
            toolbar.style.background = theme[4];
            const nameSpan = document.getElementById('nameSpan');
            if (nameSpan) {
                nameSpan.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
            }
            const logo = document.getElementById('logo') as HTMLImageElement;
            if (logo) {
                logo.src = theme[10] === 'black_font' ? blackNameLogo : whiteNameLogo;
            }
            // Update zoom button colors
            const zoomInBtn = document.getElementById('zoomInBtn');
            const zoomOutBtn = document.getElementById('zoomOutBtn');
            const zoomWidthBtn = document.getElementById('zoomWidthBtn');
            const zoomPageBtn = document.getElementById('zoomPageBtn');
            if (zoomInBtn && zoomOutBtn) {
                const buttonColor = theme[10] === 'black_font' ? '#000' : '#fff';
                zoomInBtn.style.color = buttonColor;
                zoomOutBtn.style.color = buttonColor;
                if (zoomWidthBtn) zoomWidthBtn.style.color = buttonColor;
                if (zoomPageBtn) zoomPageBtn.style.color = buttonColor;
            }
            // Update page navigation and history button colors
            const backBtn = document.getElementById('backBtn');
            const forwardBtn = document.getElementById('forwardBtn');
            const prevPageBtn = document.getElementById('prevPageBtn');
            const nextPageBtn = document.getElementById('nextPageBtn');
            const findToggleBtn = document.getElementById('findToggleBtn');
            if (backBtn || forwardBtn || prevPageBtn || nextPageBtn || findToggleBtn) {
                const buttonColor = theme[10] === 'black_font' ? '#000' : '#fff';
                if (backBtn) backBtn.style.color = buttonColor;
                if (forwardBtn) forwardBtn.style.color = buttonColor;
                if (prevPageBtn) prevPageBtn.style.color = buttonColor;
                if (nextPageBtn) nextPageBtn.style.color = buttonColor;
                if (findToggleBtn) findToggleBtn.style.color = buttonColor;
            }
            // Update separator colors
            const separators = document.querySelectorAll('#viewerTabToolbar > div > div');
            separators.forEach((sep) => {
                const sepDiv = sep as HTMLDivElement;
                if (sepDiv.style.width === '1px') {
                    sepDiv.style.background = theme[10] === 'black_font' ? '#ccc' : '#666';
                }
            });
            // Update page input field colors
            const pageInput = document.getElementById('pageInput') as HTMLInputElement;
            if (pageInput) {
                pageInput.style.background = '#fff';
                pageInput.style.color = '#000';
                pageInput.style.border = '1px solid #ccc';
            }
            // Update page label color
            const pageLabel = document.getElementById('pageLabel');
            if (pageLabel) {
                pageLabel.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
            }
            // Update bookmark sidebar colors
            const sidebar = document.getElementById('bookmarkSidebar');
            if (sidebar) {
                // Use theme colors for more aesthetic sidebar (both light and dark themes)
                sidebar.style.background = theme[10] === 'black_font' ? theme[6] : theme[4];
                sidebar.style.borderRight = `1px solid ${theme[10] === 'black_font' ? theme[2] : theme[1]}`;

                // Re-render outline with new theme colors
                if (pdfOutline && pdfOutline.length > 0) {
                    renderOutline();
                }
            }
        }
    }
}

function setInitialTheme() {
    // set inital theme
    if (Utils.isWindows()) {
        const theme: string[] = [];
        theme[1] = '#777';
        theme[2] = '#999';
        theme[3] = '#999';
        theme[4] = '#555';
        theme[5] = '#555';
        theme[6] = '#555';
        theme[7] = 'transparent';
        theme[8] = '#333';
        theme[9] = '#ffffff'; // Set default background color to white
        setTheme(theme);
    }
}

export const setFindHighlights = () => {
    // Query for all highlighted elements.
    const elements = document.querySelectorAll('.textLayer .highlight');
    // console.log('elements', elements);

    // Loop through all elements and set the highlight color.
    for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        element.style.backgroundColor = 'rgb(0, 0, 255, 0.25)';
    }
};

// Patch setTheme to add toolbar if in viewerTab mode
const originalSetTheme = setTheme;
function setThemeWithToolbar(theme: string[]) {
    originalSetTheme(theme);
    if (viewerMode === 'viewerTab') {
        addViewerTabToolbar(theme, viewerDocName);
    }
}

// Handle zoom button clicks
function handleZoomClick(delta: number) {
    if (!pagesinit) return;
    
    // Use center of viewport as zoom point
    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        const centerX = viewerContainer.clientWidth / 2;
        const centerY = viewerContainer.clientHeight / 2;
        wheelZoom(delta, centerX, centerY);
    }
}

// Handle zoom mode button clicks
async function handleZoomModeClick(zoomMode: Zoom) {
    if (!pagesinit) return;

    const scalePayload = {
        zoom: zoomMode,
        scale: 1.0,
        dualPage: loadPdfPayload.scalePayload.dualPage,
    };

    loadPdfPayload.scalePayload = scalePayload;
    await scaleHandler();
}

// Handle page navigation
function handlePrevPage() {
    if (!pagesinit || !pdfViewer) return;
    const newPage = pdfViewer.currentPageNumber - 1;
    if (newPage >= 1) {
        setCurrentPage(newPage);
        viewChanged(false, false);
    }
}

function handleNextPage() {
    if (!pagesinit || !pdfViewer || !pdfDocument) return;
    const newPage = pdfViewer.currentPageNumber + 1;
    if (newPage <= pdfDocument.numPages) {
        setCurrentPage(newPage);
        viewChanged(false, false);
    }
}

function handlePageInputChange(inputValue: string) {
    if (!pagesinit || !pdfViewer || !pdfDocument) return;
    const pageNumber = parseInt(inputValue, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= pdfDocument.numPages) {
        setCurrentPage(pageNumber);
        viewChanged(false, false);
    }
}

// Page history management functions
function addToPageHistory(pageNumber: number, replaceHistory = false) {
    // Don't add if it's the same as the current page in history
    if (pageHistory[pageHistoryIndex] === pageNumber) {
        return;
    }

    if (replaceHistory) {
        // When navigating via back/forward, don't add to history
        return;
    }

    // Remove any forward history when navigating to a new page
    pageHistory.splice(pageHistoryIndex + 1);

    // Add the new page to history
    pageHistory.push(pageNumber);
    pageHistoryIndex = pageHistory.length - 1;
}

function canGoBack(): boolean {
    return pageHistoryIndex > 0;
}

function canGoForward(): boolean {
    return pageHistoryIndex < pageHistory.length - 1;
}

function goBackInHistory() {
    if (!canGoBack() || !pagesinit) return;

    pageHistoryIndex--;
    const targetPage = pageHistory[pageHistoryIndex];
    setCurrentPage(targetPage);
    viewChanged(false, false);
}

function goForwardInHistory() {
    if (!canGoForward() || !pagesinit) return;

    pageHistoryIndex++;
    const targetPage = pageHistory[pageHistoryIndex];
    setCurrentPage(targetPage);
    viewChanged(false, false);
}

function updateHistoryButtons() {
    const { backButton, forwardButton } = pageNavigationElements;

    if (backButton) {
        backButton.disabled = !canGoBack();
        backButton.style.opacity = backButton.disabled ? '0.3' : '1';
        backButton.style.cursor = backButton.disabled ? 'default' : 'pointer';
    }

    if (forwardButton) {
        forwardButton.disabled = !canGoForward();
        forwardButton.style.opacity = forwardButton.disabled ? '0.3' : '1';
        forwardButton.style.cursor = forwardButton.disabled ? 'default' : 'pointer';
    }
}

// Progress indicator functions for viewerTab mode
function showProgress() {
    if (!progressOverlay) return;
    progressOverlay.style.display = 'flex';
    if (progressText) {
        progressText.textContent = 'Loading... 0%';
    }
}

function hideProgress() {
    if (!progressOverlay) return;
    progressOverlay.style.display = 'none';
}

function updateProgress(percentage: number) {
    if (!progressText) return;
    if (percentage > 0) {
        const rounded = Math.round(percentage);
        progressText.textContent = `Loading... ${rounded}%`;
    } else {
        // Indeterminate progress (no percentage known)
        progressText.textContent = 'Loading...';
    }
}

// Update page number display
export function updatePageDisplay() {
    if (!pagesinit || !pdfViewer || !pdfDocument) return;

    const { pageInput, prevButton, nextButton } = pageNavigationElements;

    if (pageInput) {
        pageInput.value = pdfViewer.currentPageNumber.toString();
    }

    if (prevButton) {
        prevButton.disabled = pdfViewer.currentPageNumber <= 1;
        prevButton.style.opacity = prevButton.disabled ? '0.3' : '1';
        prevButton.style.cursor = prevButton.disabled ? 'default' : 'pointer';
    }

    if (nextButton) {
        nextButton.disabled = pdfViewer.currentPageNumber >= pdfDocument.numPages;
        nextButton.style.opacity = nextButton.disabled ? '0.3' : '1';
        nextButton.style.cursor = nextButton.disabled ? 'default' : 'pointer';
    }

    // Update history buttons and add current page to history
    if (viewerMode === 'viewerTab') {
        addToPageHistory(pdfViewer.currentPageNumber);
        updateHistoryButtons();
    }
}

// Find bar functions for viewerTab mode
function openFindBar() {
    if (!findBar || !findInput) return;
    findBarOpen = true;
    findBar.style.display = 'flex';
    findInput.focus();
    findInput.select();
}

function closeFindBar() {
    if (!findBar) return;
    findBarOpen = false;
    findBar.style.display = 'none';
    lastFindQuery = '';
    if (findMatchLabel) {
        findMatchLabel.textContent = '';
    }
    // Tell the find controller to clear all highlights
    eventBus.dispatch('findbarclose', { source: null });
}

function toggleFindBar() {
    if (findBarOpen) {
        closeFindBar();
    } else {
        openFindBar();
    }
}

function executeFind(findPrevious: boolean) {
    if (!pagesinit || !findInput) return;
    const query = findInput.value;
    if (!query) {
        lastFindQuery = '';
        if (findMatchLabel) {
            findMatchLabel.textContent = '';
        }
        eventBus.dispatch('findbarclose', { source: null });
        return;
    }
    const again = query === lastFindQuery;
    lastFindQuery = query;
    eventBus.dispatch('find', {
        // 'again' advances to the next match; any other type makes PDFFindController restart the search
        type: again ? 'again' : 'find',
        query,
        highlightAll: true,
        caseSensitive: false,
        entireWord: false,
        findPrevious,
    });
}

function updateFindMatches(matchesCount: { current: number, total: number } | undefined) {
    if (!findMatchLabel || !matchesCount) return;
    if (!lastFindQuery) {
        findMatchLabel.textContent = '';
    } else if (matchesCount.total > 0) {
        findMatchLabel.textContent = `${matchesCount.current} of ${matchesCount.total}`;
    } else {
        findMatchLabel.textContent = 'No matches';
    }
    setTimeout(() => {
        setFindHighlights();
    }, 100);
}

// Add toolbar for viewerTab mode
function addViewerTabToolbar(theme: string[], docName: string) {
    // Check if toolbar already exists
    if (document.getElementById('viewerTabToolbar')) return;
    const toolbar = document.createElement('div');
    toolbar.id = 'viewerTabToolbar';
    toolbar.style.position = 'absolute';
    toolbar.style.top = '0';
    toolbar.style.left = '0';
    toolbar.style.width = '100%';
    toolbar.style.height = `${toolbarHeight}px`;
    toolbar.style.display = 'flex';
    toolbar.style.alignItems = 'center';
    toolbar.style.background = theme[4];
    toolbar.style.zIndex = '1000';
    toolbar.style.boxSizing = 'border-box';
    toolbar.style.paddingLeft = '16px';
    toolbar.style.paddingRight = '16px';
    toolbar.style.fontFamily = 'Roboto, Arial, sans-serif';

    // Logo
    const logo = document.createElement('img');
    logo.id = 'logo';
    logo.src = theme[10] === 'black_font' ? blackNameLogo : whiteNameLogo;
    logo.alt = 'Logo';
    logo.style.height = '28px';
    logo.style.width = 'auto';
    logo.style.maxWidth = '160px';
    logo.style.display = 'block';
    logo.style.marginRight = '16px';
    logo.style.flexShrink = '0';
    // Fallback: if image fails to load, try with './'
    logo.onerror = () => {
        logo.onerror = null;
        logo.src = theme[10] === 'black_font' ? blackNameLogo : whiteNameLogo;
    };
    toolbar.appendChild(logo);

    // Doc name (centered)
    const nameSpan = document.createElement('span');
    nameSpan.id = 'nameSpan';
    nameSpan.textContent = docName;
    nameSpan.style.fontSize = '18px';
    nameSpan.style.fontWeight = '400';
    nameSpan.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    nameSpan.style.overflow = 'hidden';
    nameSpan.style.textOverflow = 'ellipsis';
    nameSpan.style.whiteSpace = 'nowrap';
    nameSpan.style.fontFamily = 'Roboto, Arial, sans-serif';
    // Flex item filling the space between logo and controls: the title centers in
    // that space and ellipsizes when it runs out, so it can never overlap them
    nameSpan.style.flex = '1 1 auto';
    nameSpan.style.minWidth = '0'; // Allow the flex item to shrink below its text width
    nameSpan.style.textAlign = 'center';
    nameSpan.style.margin = '0 16px';
    toolbar.appendChild(nameSpan);

    // Page navigation controls (to the left of zoom controls)
    const pageNavContainer = document.createElement('div');
    pageNavContainer.style.display = 'flex';
    pageNavContainer.style.alignItems = 'center';
    pageNavContainer.style.gap = '8px';
    pageNavContainer.style.flexShrink = '0';
    pageNavContainer.style.marginRight = '16px';

    // Find (search) toggle button
    const findToggleBtn = document.createElement('button');
    findToggleBtn.id = 'findToggleBtn';
    findToggleBtn.innerHTML = '<span class="material-icons">search</span>';
    findToggleBtn.style.border = 'none';
    findToggleBtn.style.background = 'transparent';
    findToggleBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    findToggleBtn.style.cursor = 'pointer';
    findToggleBtn.style.padding = '6px';
    findToggleBtn.style.borderRadius = '4px';
    findToggleBtn.style.display = 'flex';
    findToggleBtn.style.alignItems = 'center';
    findToggleBtn.style.justifyContent = 'center';
    findToggleBtn.style.fontSize = '20px';
    findToggleBtn.title = 'Find in Document (Ctrl+F)';
    findToggleBtn.addEventListener('click', toggleFindBar);
    pageNavContainer.appendChild(findToggleBtn);

    // Separator between find and history buttons
    const findSeparator = document.createElement('div');
    findSeparator.style.width = '1px';
    findSeparator.style.height = '20px';
    findSeparator.style.background = theme[10] === 'black_font' ? '#ccc' : '#666';
    findSeparator.style.margin = '0 4px';
    pageNavContainer.appendChild(findSeparator);

    // Back button (history)
    const backBtn = document.createElement('button');
    backBtn.id = 'backBtn';
    backBtn.innerHTML = '<span class="material-icons">undo</span>';
    backBtn.style.border = 'none';
    backBtn.style.background = 'transparent';
    backBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    backBtn.style.cursor = 'pointer';
    backBtn.style.padding = '6px';
    backBtn.style.borderRadius = '4px';
    backBtn.style.display = 'flex';
    backBtn.style.alignItems = 'center';
    backBtn.style.justifyContent = 'center';
    backBtn.style.fontSize = '20px';
    backBtn.title = 'Back in History';
    backBtn.disabled = true;
    backBtn.style.opacity = '0.3';
    backBtn.addEventListener('click', goBackInHistory);
    pageNavContainer.appendChild(backBtn);

    // Forward button (history)
    const forwardBtn = document.createElement('button');
    forwardBtn.id = 'forwardBtn';
    forwardBtn.innerHTML = '<span class="material-icons">redo</span>';
    forwardBtn.style.border = 'none';
    forwardBtn.style.background = 'transparent';
    forwardBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    forwardBtn.style.cursor = 'pointer';
    forwardBtn.style.padding = '6px';
    forwardBtn.style.borderRadius = '4px';
    forwardBtn.style.display = 'flex';
    forwardBtn.style.alignItems = 'center';
    forwardBtn.style.justifyContent = 'center';
    forwardBtn.style.fontSize = '20px';
    forwardBtn.title = 'Forward in History';
    forwardBtn.disabled = true;
    forwardBtn.style.opacity = '0.3';
    forwardBtn.addEventListener('click', goForwardInHistory);
    pageNavContainer.appendChild(forwardBtn);

    // Add a visual separator between history and page navigation
    const separator = document.createElement('div');
    separator.style.width = '1px';
    separator.style.height = '20px';
    separator.style.background = theme[10] === 'black_font' ? '#ccc' : '#666';
    separator.style.margin = '0 4px';
    pageNavContainer.appendChild(separator);

    // Previous page button
    const prevPageBtn = document.createElement('button');
    prevPageBtn.id = 'prevPageBtn';
    prevPageBtn.innerHTML = '<span class="material-icons">arrow_back</span>';
    prevPageBtn.style.border = 'none';
    prevPageBtn.style.background = 'transparent';
    prevPageBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    prevPageBtn.style.cursor = 'pointer';
    prevPageBtn.style.padding = '6px';
    prevPageBtn.style.borderRadius = '4px';
    prevPageBtn.style.display = 'flex';
    prevPageBtn.style.alignItems = 'center';
    prevPageBtn.style.justifyContent = 'center';
    prevPageBtn.style.fontSize = '20px';
    prevPageBtn.title = 'Previous Page';
    prevPageBtn.addEventListener('click', handlePrevPage);
    pageNavContainer.appendChild(prevPageBtn);

    // Page input field
    const pageInput = document.createElement('input');
    pageInput.id = 'pageInput';
    pageInput.type = 'text';
    pageInput.value = '1';
    pageInput.style.width = '50px';
    pageInput.style.textAlign = 'center';
    pageInput.style.fontSize = '14px';
    pageInput.style.padding = '4px';
    pageInput.style.border = '1px solid #ccc';
    pageInput.style.borderRadius = '4px';
    pageInput.style.background = '#fff';
    pageInput.style.color = '#000';
    pageInput.style.fontFamily = 'Roboto, Arial, sans-serif';
    pageInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            handlePageInputChange(pageInput.value);
            pageInput.blur();
        }
    });
    pageInput.addEventListener('blur', () => {
        // Revert to current page if invalid input
        if (pdfViewer) {
            pageInput.value = pdfViewer.currentPageNumber.toString();
        }
    });
    pageNavContainer.appendChild(pageInput);

    // "of X" label
    const pageLabel = document.createElement('span');
    pageLabel.id = 'pageLabel';
    pageLabel.textContent = 'of ?';
    pageLabel.style.fontSize = '14px';
    pageLabel.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    pageLabel.style.fontFamily = 'Roboto, Arial, sans-serif';
    pageLabel.style.whiteSpace = 'nowrap';
    pageNavContainer.appendChild(pageLabel);

    // Next page button
    const nextPageBtn = document.createElement('button');
    nextPageBtn.id = 'nextPageBtn';
    nextPageBtn.innerHTML = '<span class="material-icons">arrow_forward</span>';
    nextPageBtn.style.border = 'none';
    nextPageBtn.style.background = 'transparent';
    nextPageBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    nextPageBtn.style.cursor = 'pointer';
    nextPageBtn.style.padding = '6px';
    nextPageBtn.style.borderRadius = '4px';
    nextPageBtn.style.display = 'flex';
    nextPageBtn.style.alignItems = 'center';
    nextPageBtn.style.justifyContent = 'center';
    nextPageBtn.style.fontSize = '20px';
    nextPageBtn.title = 'Next Page';
    nextPageBtn.addEventListener('click', handleNextPage);
    pageNavContainer.appendChild(nextPageBtn);

    toolbar.appendChild(pageNavContainer);

    // Store references for later updates
    pageNavigationElements.pageInput = pageInput;
    pageNavigationElements.prevButton = prevPageBtn;
    pageNavigationElements.nextButton = nextPageBtn;
    pageNavigationElements.backButton = backBtn;
    pageNavigationElements.forwardButton = forwardBtn;

    // Update page label when document is loaded
    if (pdfDocument && pdfDocument.numPages) {
        pageLabel.textContent = `of ${pdfDocument.numPages}`;
        updatePageDisplay();
    }

    // Zoom controls (right side)
    const zoomControls = document.createElement('div');
    zoomControls.style.display = 'flex';
    zoomControls.style.alignItems = 'center';
    zoomControls.style.gap = '0px';
    zoomControls.style.flexShrink = '0';

    // Zoom out button
    const zoomOutBtn = document.createElement('button');
    zoomOutBtn.id = 'zoomOutBtn';
    zoomOutBtn.innerHTML = '<span class="material-icons">remove_circle_outline</span>';
    zoomOutBtn.style.border = 'none';
    zoomOutBtn.style.background = 'transparent';
    zoomOutBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    zoomOutBtn.style.cursor = 'pointer';
    zoomOutBtn.style.padding = '8px';
    zoomOutBtn.style.borderRadius = '4px';
    zoomOutBtn.style.display = 'flex';
    zoomOutBtn.style.alignItems = 'center';
    zoomOutBtn.style.justifyContent = 'center';
    zoomOutBtn.title = 'Zoom Out';
    zoomOutBtn.addEventListener('click', () => handleZoomClick(1));
    zoomControls.appendChild(zoomOutBtn);

    // Zoom in button
    const zoomInBtn = document.createElement('button');
    zoomInBtn.id = 'zoomInBtn';
    zoomInBtn.innerHTML = '<span class="material-icons">add_circle_outline</span>';
    zoomInBtn.style.border = 'none';
    zoomInBtn.style.background = 'transparent';
    zoomInBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    zoomInBtn.style.cursor = 'pointer';
    zoomInBtn.style.padding = '8px';
    zoomInBtn.style.borderRadius = '4px';
    zoomInBtn.style.display = 'flex';
    zoomInBtn.style.alignItems = 'center';
    zoomInBtn.style.justifyContent = 'center';
    zoomInBtn.title = 'Zoom In';
    zoomInBtn.addEventListener('click', () => handleZoomClick(-1));
    zoomControls.appendChild(zoomInBtn);

    // Zoom width button
    const zoomWidthBtn = document.createElement('button');
    zoomWidthBtn.id = 'zoomWidthBtn';
    zoomWidthBtn.innerHTML = '<span class="icon-zoom-width"></span>';
    zoomWidthBtn.style.border = 'none';
    zoomWidthBtn.style.background = 'transparent';
    zoomWidthBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    zoomWidthBtn.style.cursor = 'pointer';
    zoomWidthBtn.style.padding = '8px';
    zoomWidthBtn.style.borderRadius = '4px';
    zoomWidthBtn.style.display = 'flex';
    zoomWidthBtn.style.alignItems = 'center';
    zoomWidthBtn.style.justifyContent = 'center';
    zoomWidthBtn.style.fontSize = '24px';
    zoomWidthBtn.title = 'Zoom to Width';
    zoomWidthBtn.setAttribute('aria-label', 'Zoom to Width');
    zoomWidthBtn.addEventListener('click', () => handleZoomModeClick(Zoom.WIDTH));
    zoomControls.appendChild(zoomWidthBtn);

    // Zoom page button
    const zoomPageBtn = document.createElement('button');
    zoomPageBtn.id = 'zoomPageBtn';
    zoomPageBtn.innerHTML = '<span class="icon-zoom-page"></span>';
    zoomPageBtn.style.border = 'none';
    zoomPageBtn.style.background = 'transparent';
    zoomPageBtn.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    zoomPageBtn.style.cursor = 'pointer';
    zoomPageBtn.style.padding = '8px';
    zoomPageBtn.style.borderRadius = '4px';
    zoomPageBtn.style.display = 'flex';
    zoomPageBtn.style.alignItems = 'center';
    zoomPageBtn.style.justifyContent = 'center';
    zoomPageBtn.style.fontSize = '24px';
    zoomPageBtn.title = 'Zoom to Page';
    zoomPageBtn.addEventListener('click', () => handleZoomModeClick(Zoom.PAGE));
    zoomControls.appendChild(zoomPageBtn);

    toolbar.appendChild(zoomControls);

    // Insert toolbar at the top of the body
    document.body.appendChild(toolbar);

    // Move viewerContainer down
    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        viewerContainer.style.top = '44px';
        viewerContainer.style.height = 'calc(100% - 44px)';
    }

    // Create find bar (hidden by default, toggled with the search button or Ctrl/Cmd+F)
    const findBarDiv = document.createElement('div');
    findBarDiv.id = 'findBar';
    findBarDiv.style.position = 'fixed';
    findBarDiv.style.top = `${toolbarHeight + 4}px`;
    findBarDiv.style.right = '16px';
    findBarDiv.style.display = 'none';
    findBarDiv.style.alignItems = 'center';
    findBarDiv.style.gap = '6px';
    findBarDiv.style.background = '#fff';
    findBarDiv.style.border = '1px solid #ccc';
    findBarDiv.style.borderRadius = '4px';
    findBarDiv.style.padding = '6px 8px';
    findBarDiv.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.25)';
    findBarDiv.style.zIndex = '1001';
    findBarDiv.style.fontFamily = 'Roboto, Arial, sans-serif';

    const findInputField = document.createElement('input');
    findInputField.id = 'findInput';
    findInputField.type = 'text';
    findInputField.placeholder = 'Find in document';
    findInputField.style.width = '180px';
    findInputField.style.fontSize = '14px';
    findInputField.style.padding = '4px';
    findInputField.style.border = '1px solid #ccc';
    findInputField.style.borderRadius = '4px';
    findInputField.style.background = '#fff';
    findInputField.style.color = '#000';
    findInputField.style.fontFamily = 'Roboto, Arial, sans-serif';
    findInputField.addEventListener('input', () => {
        if (findDebounceTimeout) {
            clearTimeout(findDebounceTimeout);
        }
        findDebounceTimeout = setTimeout(() => executeFind(false), 250);
    });
    findInputField.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            executeFind(e.shiftKey);
        }
    });
    findBarDiv.appendChild(findInputField);

    const findMatchSpan = document.createElement('span');
    findMatchSpan.id = 'findMatchLabel';
    findMatchSpan.style.fontSize = '13px';
    findMatchSpan.style.color = '#555';
    findMatchSpan.style.whiteSpace = 'nowrap';
    findMatchSpan.style.minWidth = '60px';
    findMatchSpan.style.textAlign = 'center';
    findBarDiv.appendChild(findMatchSpan);

    // Previous match button
    const findPrevBtn = document.createElement('button');
    findPrevBtn.id = 'findPrevBtn';
    findPrevBtn.innerHTML = '<span class="material-icons">keyboard_arrow_up</span>';
    findPrevBtn.style.border = 'none';
    findPrevBtn.style.background = 'transparent';
    findPrevBtn.style.color = '#444';
    findPrevBtn.style.cursor = 'pointer';
    findPrevBtn.style.padding = '4px';
    findPrevBtn.style.borderRadius = '4px';
    findPrevBtn.style.display = 'flex';
    findPrevBtn.style.alignItems = 'center';
    findPrevBtn.style.justifyContent = 'center';
    findPrevBtn.style.fontSize = '20px';
    findPrevBtn.title = 'Previous Match (Shift+Enter)';
    findPrevBtn.addEventListener('click', () => executeFind(true));
    findBarDiv.appendChild(findPrevBtn);

    // Next match button
    const findNextBtn = document.createElement('button');
    findNextBtn.id = 'findNextBtn';
    findNextBtn.innerHTML = '<span class="material-icons">keyboard_arrow_down</span>';
    findNextBtn.style.border = 'none';
    findNextBtn.style.background = 'transparent';
    findNextBtn.style.color = '#444';
    findNextBtn.style.cursor = 'pointer';
    findNextBtn.style.padding = '4px';
    findNextBtn.style.borderRadius = '4px';
    findNextBtn.style.display = 'flex';
    findNextBtn.style.alignItems = 'center';
    findNextBtn.style.justifyContent = 'center';
    findNextBtn.style.fontSize = '20px';
    findNextBtn.title = 'Next Match (Enter)';
    findNextBtn.addEventListener('click', () => executeFind(false));
    findBarDiv.appendChild(findNextBtn);

    // Close button
    const findCloseBtn = document.createElement('button');
    findCloseBtn.id = 'findCloseBtn';
    findCloseBtn.innerHTML = '<span class="material-icons">close</span>';
    findCloseBtn.style.border = 'none';
    findCloseBtn.style.background = 'transparent';
    findCloseBtn.style.color = '#444';
    findCloseBtn.style.cursor = 'pointer';
    findCloseBtn.style.padding = '4px';
    findCloseBtn.style.borderRadius = '4px';
    findCloseBtn.style.display = 'flex';
    findCloseBtn.style.alignItems = 'center';
    findCloseBtn.style.justifyContent = 'center';
    findCloseBtn.style.fontSize = '20px';
    findCloseBtn.title = 'Close (Escape)';
    findCloseBtn.addEventListener('click', closeFindBar);
    findBarDiv.appendChild(findCloseBtn);

    document.body.appendChild(findBarDiv);

    findBar = findBarDiv;
    findInput = findInputField;
    findMatchLabel = findMatchSpan;

    // Update find UI as matches are reported by the find controller
    eventBus._on('updatefindmatchescount', ({ matchesCount }) => updateFindMatches(matchesCount));
    eventBus._on('updatefindcontrolstate', ({ matchesCount }) => updateFindMatches(matchesCount));

    // Create progress indicator overlay
    const overlay = document.createElement('div');
    overlay.id = 'progressOverlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = theme[10] === 'black_font' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.7)';
    overlay.style.display = 'none'; // Hidden by default
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.style.zIndex = '2000'; // Above toolbar
    overlay.style.flexDirection = 'column';

    // Progress container
    const progressContainer = document.createElement('div');
    progressContainer.style.display = 'flex';
    progressContainer.style.flexDirection = 'column';
    progressContainer.style.alignItems = 'center';
    progressContainer.style.gap = '16px';

    // Spinner
    const spinner = document.createElement('div');
    spinner.style.width = '60px';
    spinner.style.height = '60px';
    // Use light colors for dark background, dark colors for light background
    spinner.style.border = `6px solid ${theme[10] === 'black_font' ? '#ddd' : 'rgba(255, 255, 255, 0.3)'}`;
    spinner.style.borderTop = `6px solid ${theme[10] === 'black_font' ? theme[4] : '#ffffff'}`; // Bright white for dark theme
    spinner.style.borderRadius = '50%';
    spinner.style.animation = 'spin 1s linear infinite';
    progressContainer.appendChild(spinner);

    // Add CSS keyframe animation for spinner
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);

    // Progress text
    const text = document.createElement('span');
    text.id = 'progressText';
    text.textContent = 'Loading... 0%';
    text.style.fontSize = '18px';
    text.style.fontWeight = '500';
    text.style.color = theme[10] === 'black_font' ? '#000' : '#fff';
    text.style.fontFamily = 'Roboto, Arial, sans-serif';
    progressContainer.appendChild(text);

    overlay.appendChild(progressContainer);
    document.body.appendChild(overlay);

    // Create bookmark sidebar
    const sidebar = document.createElement('div');
    sidebar.id = 'bookmarkSidebar';
    sidebar.style.position = 'fixed';
    sidebar.style.left = '0';
    sidebar.style.top = `${toolbarHeight}px`;
    sidebar.style.width = '0px'; // Hidden by default
    sidebar.style.height = `calc(100% - ${toolbarHeight}px)`;
    // Use theme colors for more aesthetic sidebar (both light and dark themes)
    sidebar.style.background = theme[10] === 'black_font' ? theme[6] : theme[4];
    sidebar.style.borderRight = `1px solid ${theme[10] === 'black_font' ? theme[2] : theme[1]}`;
    sidebar.style.overflowX = 'hidden';
    sidebar.style.overflowY = 'auto';
    sidebar.style.transition = 'width 0.3s ease';
    sidebar.style.zIndex = '900';
    sidebar.style.boxSizing = 'border-box';

    // Bookmark content container
    const content = document.createElement('div');
    content.id = 'bookmarkContent';
    content.style.padding = '8px';
    content.style.paddingRight = '12px'; // Extra padding for resize handle
    content.style.minWidth = '250px'; // Ensures content has proper width when sidebar expands
    sidebar.appendChild(content);

    // Resize handle (fixed position so it doesn't scroll with content)
    const resizeHandle = document.createElement('div');
    resizeHandle.id = 'bookmarkResizeHandle';
    resizeHandle.style.position = 'fixed';
    resizeHandle.style.right = 'auto'; // Will be calculated dynamically
    resizeHandle.style.top = `${toolbarHeight}px`;
    resizeHandle.style.width = '4px';
    resizeHandle.style.height = `calc(100% - ${toolbarHeight}px)`;
    resizeHandle.style.cursor = 'col-resize';
    resizeHandle.style.background = 'transparent';
    resizeHandle.style.zIndex = '901';
    resizeHandle.style.pointerEvents = 'auto';

    // Add hover effect
    resizeHandle.addEventListener('mouseenter', () => {
        resizeHandle.style.background = theme[10] === 'black_font' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)';
    });
    resizeHandle.addEventListener('mouseleave', () => {
        resizeHandle.style.background = 'transparent';
    });

    // Resize functionality
    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
        isResizing = true;
        startX = e.clientX;
        startWidth = sidebarWidth;
        e.preventDefault();

        // Disable transitions during resize for smooth dragging
        sidebar.style.transition = 'none';
        const viewerContainer = document.getElementById('viewerContainer');
        if (viewerContainer) {
            viewerContainer.style.transition = 'none';
        }
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isResizing || !sidebar) return;

        const delta = e.clientX - startX;
        const newWidth = Math.max(150, Math.min(600, startWidth + delta)); // Min 150px, Max 600px

        sidebarWidth = newWidth;
        sidebar.style.width = `${newWidth}px`;

        // Update resize handle position
        resizeHandle.style.left = `${newWidth - 2}px`;

        // Adjust viewer container
        const viewerContainer = document.getElementById('viewerContainer');
        if (viewerContainer) {
            viewerContainer.style.left = `${newWidth}px`;
            viewerContainer.style.width = `calc(100% - ${newWidth}px)`;
        }
    });

    document.addEventListener('mouseup', () => {
        if (!isResizing) return;

        isResizing = false;

        // Re-enable transitions
        if (sidebar) {
            sidebar.style.transition = 'width 0.3s ease';
        }
        const viewerContainer = document.getElementById('viewerContainer');
        if (viewerContainer) {
            viewerContainer.style.transition = 'left 0.3s ease, width 0.3s ease';
        }

        // Redraw page borders with new width
        viewChanged(false, false);
    });

    document.body.appendChild(sidebar);
    document.body.appendChild(resizeHandle); // Append to body so it's fixed and doesn't scroll with sidebar content

    // Function to update resize handle position (store globally so renderOutline can use it)
    updateResizeHandlePosition = () => {
        if (sidebarOpen && resizeHandle) {
            resizeHandle.style.left = `${sidebarWidth - 2}px`; // Position at right edge of sidebar
            resizeHandle.style.display = 'block';
        } else {
            resizeHandle.style.display = 'none';
        }
    };

    // Initial position update
    updateResizeHandlePosition();

    // Store references
    progressOverlay = overlay;
    progressText = text;
    bookmarkSidebar = sidebar;
    bookmarkContent = content;
}

// Render the outline/bookmarks into the sidebar
function renderOutline() {
    if (!bookmarkContent || !bookmarkSidebar) return;

    // Clear existing content
    bookmarkContent.innerHTML = '';

    // Check if outline exists and has items
    if (!pdfOutline || pdfOutline.length === 0) {
        // No bookmarks, hide the sidebar
        sidebarOpen = false;
        bookmarkSidebar.style.width = '0px';

        // Reset viewer container
        const viewerContainer = document.getElementById('viewerContainer');
        if (viewerContainer) {
            viewerContainer.style.left = '0';
            viewerContainer.style.width = '100%';
        }

        // Update resize handle position
        if (updateResizeHandlePosition) {
            updateResizeHandlePosition();
        }
        return;
    }

    // PDF has bookmarks, show the sidebar automatically
    sidebarOpen = true;
    bookmarkSidebar.style.width = `${sidebarWidth}px`;

    // Adjust viewer container to make room for sidebar
    const viewerContainer = document.getElementById('viewerContainer');
    if (viewerContainer) {
        viewerContainer.style.left = `${sidebarWidth}px`;
        viewerContainer.style.width = `calc(100% - ${sidebarWidth}px)`;
    }

    // Update resize handle position
    if (updateResizeHandlePosition) {
        updateResizeHandlePosition();
    }

    // Build the bookmark tree
    const tree = buildOutlineTree(pdfOutline, 0);
    bookmarkContent.appendChild(tree);

    // Redraw page border with sidebar offset
    viewChanged(false, false);
}

// Recursively build the outline tree HTML
function buildOutlineTree(items: any[], level: number): HTMLElement {
    const container = document.createElement('div');
    container.style.marginLeft = level > 0 ? '16px' : '0';

    for (const item of items) {
        const itemDiv = document.createElement('div');
        itemDiv.style.marginBottom = '4px';

        // Create the bookmark item element
        const itemButton = document.createElement('div');
        itemButton.style.display = 'flex';
        itemButton.style.alignItems = 'center';
        itemButton.style.padding = '6px 8px';
        itemButton.style.cursor = 'pointer';
        itemButton.style.borderRadius = '4px';
        itemButton.style.fontSize = '13px';
        itemButton.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
        itemButton.style.lineHeight = '1.4';
        itemButton.style.wordBreak = 'break-word';
        itemButton.style.transition = 'background 0.2s';

        // Get theme colors - determine if light or dark theme
        // Light themes have theme[10] === 'black_font', dark themes have 'white_font' or undefined
        let isLightTheme = currentTheme && currentTheme[10] === 'black_font';

        // Fallback: check sidebar background color brightness if theme check is ambiguous
        if (!isLightTheme && bookmarkSidebar) {
            const bgColor = bookmarkSidebar.style.background;
            // Check if background is light colored (rgb values > 100 indicate light theme)
            // Light themes: rgb(171+, ..., ...) Dark themes: rgb(42, 42, 42) or #2a2a2a
            if (bgColor && bgColor.startsWith('rgb')) {
                const match = bgColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (match) {
                    const r = parseInt(match[1]);
                    // If red channel is > 100, it's likely a light theme
                    isLightTheme = r > 100;
                }
            }
        }

        // console.log('buildOutlineTree: currentTheme[10]=', currentTheme ? currentTheme[10] : 'undefined', 'sidebar bg=', bookmarkSidebar?.style.background, 'isLightTheme=', isLightTheme);

        itemButton.style.color = isLightTheme ? '#000' : '#fff';

        // Hover effect
        itemButton.addEventListener('mouseenter', () => {
            itemButton.style.background = isLightTheme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)';
        });
        itemButton.addEventListener('mouseleave', () => {
            itemButton.style.background = 'transparent';
        });

        // Check if item has children
        const hasChildren = item.items && item.items.length > 0;

        if (hasChildren) {
            // Add expand/collapse icon
            const icon = document.createElement('span');
            icon.className = 'material-icons';
            icon.textContent = 'chevron_right';
            icon.style.fontSize = '18px';
            icon.style.marginRight = '4px';
            icon.style.transition = 'transform 0.2s';
            itemButton.appendChild(icon);

            // Add click handler for expand/collapse
            let expanded = false;
            const childContainer = buildOutlineTree(item.items, level + 1);
            childContainer.style.display = 'none';

            icon.addEventListener('click', (e) => {
                e.stopPropagation();
                expanded = !expanded;
                icon.style.transform = expanded ? 'rotate(90deg)' : 'rotate(0deg)';
                childContainer.style.display = expanded ? 'block' : 'none';
            });

            itemDiv.appendChild(childContainer);
        } else {
            // Add spacing to align with items that have icons
            const spacer = document.createElement('span');
            spacer.style.width = '22px';
            spacer.style.display = 'inline-block';
            itemButton.appendChild(spacer);
        }

        // Add title text
        const titleSpan = document.createElement('span');
        titleSpan.textContent = item.title || 'Untitled';
        titleSpan.style.flex = '1';
        titleSpan.style.overflow = 'hidden';
        titleSpan.style.textOverflow = 'ellipsis';
        titleSpan.title = item.title || 'Untitled'; // Tooltip for long titles
        itemButton.appendChild(titleSpan);

        // Add click handler for navigation
        itemButton.addEventListener('click', () => navigateToBookmark(item));

        itemDiv.insertBefore(itemButton, itemDiv.firstChild);
        container.appendChild(itemDiv);
    }

    return container;
}

// Navigate to the page specified in the bookmark
async function navigateToBookmark(item: any) {
    if (!pdfDocument || !item.dest) return;

    try {
        let dest = item.dest;

        // If dest is a string, get the actual destination
        if (typeof dest === 'string') {
            dest = await pdfDocument.getDestination(dest);
        }

        if (!dest || !Array.isArray(dest)) return;

        // The first element of the destination array is the page reference
        const pageRef = dest[0];

        // Get the page index from the reference
        const pageIndex = await pdfDocument.getPageIndex(pageRef);
        const pageNumber = pageIndex + 1; // Page numbers are 1-based

        // Navigate to the page
        if (pageNumber > 0 && pageNumber <= pdfDocument.numPages) {
            setCurrentPage(pageNumber);
            viewChanged(false, false);
        }
    } catch (error) {
        console.warn('Failed to navigate to bookmark:', error);
    }
}

